import { ReactNode } from "react";

import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";

import { IGroupBy as GlobalGroupBy, INormalizeBySetting } from "models";
import { IColorPalette } from "models/colorPalette";

import { ChartTypeLabels } from "../constants";
import { mBinSize } from "./binSize";
import { IScreenshotSettings } from "./screenshot";

export type IAxisMinMax = {
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  y2Min?: number;
  y2Max?: number;
};

export type ChartProductGroupT =
  | "Production"
  | "Sales"
  | "Throughput"
  | "Shrinkage"
  | "Injection";

export interface IBin {
  binSize: number;

  lessThan: number;
  greaterThan: number;

  minSize: number;
  maxBins?: number;

  maxBinsSortOrder?: string;
}

export interface IGroupBy {
  property: string; // groupBy category
  title: string; // groupBy field
  id: number; // groupBy field Id

  color: string;
  canBin: boolean;

  dataType: number;

  // position groupBy component
  place: string;
  align: string;
}

export interface IChart {
  key: string;

  traces: [];
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  legendTitle: string;

  product: string;
  chartType: string;
  chartTypeId: number;

  bin: IBin;
  binVisible: boolean;

  groupBy: IGroupBy;
  groupByVisible: boolean;

  range: {
    x: [];
    y: [];
  };
  rangeVisible: boolean;

  // toolbar icons
  producing: boolean;
  showActiveWellsOnly: boolean;
  forecast: boolean;
  average: boolean;
  daily: boolean;
  palette: boolean;
  snapshot: boolean;
  legend: boolean;
  fullscreen: boolean;
  cutoffPercent: number;
  sum: boolean;
  survivorBias: boolean;
  useWeightedRatioAverage: boolean;
  // x and y axis scale
  log: {
    x: boolean;
    y: boolean;
  };

  // x and y axis groupBy params
  chartTypeParam: {
    x: IGroupBy;
    y: IGroupBy;
  };
  xAxisParamVisible: boolean;
  xAxisParamListVisible: boolean;
  yAxisParamVisible: boolean;
  yAxisParamListVisible: boolean;

  // loading indicator
  busy: boolean;
}

export type slopeLines = {
  halfSlope: Line;
  quarterSlope: Line;
  unitSlope: Line;
};

export type Line = {
  point1: Point;
  point2: Point;
};

export type Point = {
  x: number;
  y: number;
};

export interface AxisLockStatus {
  x: boolean;
  y: boolean;
}

export type ChartAxisType = "x" | "y";

export interface IChartType {
  label: ChartTypeLabels;
  id: number;
  lock?: { x: boolean; y: boolean };
  tooltip?: string;
}

export interface IProductType {
  key: string;
  label: string;
  id: number;
  lock?: number | null;
  // Callback receives a hasFeature accessor (currently always `hasMidstream`,
  // backed by useFeatureFlags) that takes a plain feature/flag name string.
  available?: (hasFeature?: (feature: string) => boolean) => boolean;
  icon: ReactNode;
  tooltip?: string;
  midstreamTooltip?: string;
  unit?: string;
}

export interface AverageSeriesStyle {
  color: string;
  thickness: number;
}

export interface SlopeLineStyle {
  color: string;
  thickness: number;
  length: number;
}

export type lineCoordinates = {
  p1: Point;
  p2: Point;
  chartP1: Point;
  chartP2: Point;
};

export enum SlopeTypes {
  Unit = "unit",
  Half = "half",
  Quarter = "quarter"
}

export interface IAllChartTypes {
  CrossPlot: IChartType;
  RateCum: IChartType;
  RateTime: IChartType;
  RateDate: IChartType;
  TotalRateDate: IChartType;
  CAGR: IChartType;
  BaseDeclineRate: IChartType;
  Probit: IChartType;
  BoxPlot: IChartType;
  Pie: IChartType;
  StackedBar: IChartType;
  StackedBarHorizontal: IChartType;
  Mosaic: IChartType;
  MaterialBalanceTime: IChartType;
  CumTime: IChartType;
  TotalRateCum: IChartType;
  TrendDate: IChartType;
  WellContribution: IChartType;
  RecoveryPVI: IChartType;
  RecoveryTime: IChartType;
  InjectorTotal: IChartType;
  RigFracActivity: IChartType;
  RigFracRecentActivity: IChartType;
  WellStatusOverTime: IChartType;
  WellStatusCurrent: IChartType;
}

export interface IAllProductTypes {
  // primary
  Oil: IProductType;
  Gas: IProductType;
  Water: IProductType;
  TotalFluid: IProductType;
  BOE: IProductType;
  BOE20: IProductType;
  BOE30: IProductType;
  WaterInj: IProductType;
  GasInj: IProductType;
  SteamInj: IProductType;
  PolymerInj: IProductType;
  CO_2Inj: IProductType;
  AcidGasInj: IProductType;
  SolventInj: IProductType;
  TotalLiquidInj: IProductType;
  TotalGasInj: IProductType;
  // ratio
  CGR: IProductType;
  GOR: IProductType;
  WGR: IProductType;
  WOR: IProductType;
  OnePlusWOR: IProductType;
  ISOR: IProductType;
  CSOR: IProductType;
  IVRR: IProductType;
  VRR: IProductType;
  IWB: IProductType;
  CWB: IProductType;
  // percent
  OilCut: IProductType;
  WaterCut: IProductType;
  OnTime: IProductType;
  SalesGas: IProductType;
  Shrinkage: IProductType;
  TotalShrinkageInlet: IProductType;
  TotalShrinkageOutlet: IProductType;
  C2: IProductType;
  C3: IProductType;
  C4: IProductType;
  C5: IProductType;
  SalesLiquids: IProductType;
  OilCondC5: IProductType;
  NGLMix: IProductType;
  NGL_234: IProductType;
  C2YieldsRaw: IProductType;
  C3YieldsRaw: IProductType;
  C4YieldsRaw: IProductType;
  C5YieldsRaw: IProductType;
  OilCondC5YieldsRaw: IProductType;
  LiquidsYieldsRaw: IProductType;
  NGLMixYieldsRaw: IProductType;
  NGL_234YieldsRaw: IProductType;
  C2YieldsSales: IProductType;
  C3YieldsSales: IProductType;
  C4YieldsSales: IProductType;
  C5YieldsSales: IProductType;
  LiquidsYieldsSales: IProductType;
  OilCondC5YieldsSales: IProductType;
  NGLMixYieldsSales: IProductType;
  NGL_234YieldsSales: IProductType;
  GasEnergy: IProductType;
  GasEnergyYieldsSales: IProductType;
  SalesBOE: IProductType;
  SalesBOE20: IProductType;
  SalesBOE30: IProductType;
  Hours: IProductType;

  /*** midstream products **/
  H2S: IProductType;
  PlantShrinkage: IProductType;
  Fuel: IProductType;
  Flared: IProductType;
  Vented: IProductType;
  Sulphur: IProductType;
  SulphurYieldsInlet: IProductType;
  SulphurYieldsOutlet: IProductType;
  C2YieldsInlet: IProductType;
  C3YieldsInlet: IProductType;
  C4YieldsInlet: IProductType;
  C5YieldsInlet: IProductType;
  OilCondC5YieldsInlet: IProductType;
  LiquidsYieldsInlet: IProductType;
  NGLMixYieldsInlet: IProductType;
  NGL_234YieldsInlet: IProductType;
  C2YieldsOutlet: IProductType;
  C3YieldsOutlet: IProductType;
  C4YieldsOutlet: IProductType;
  C5YieldsOutlet: IProductType;
  LiquidsYieldsOutlet: IProductType;
  OilCondC5YieldsOutlet: IProductType;
  NGL_234YieldsOutlet: IProductType;
  NGLMixYieldsOutlet: IProductType;
  GPWater: IProductType;
  GPWaterYieldsInlet: IProductType;
  GPWaterYieldsOutlet: IProductType;
  CO_2: IProductType;
  CO_2YieldsInlet: IProductType;
  CO_2YieldsOutlet: IProductType;
  InletUtilization: IProductType;
  CasingPressure: IProductType;
  TubingPressure: IProductType;
  LiquidsShrinkage: IProductType;
  AcidGasShrinkage: IProductType;
  AcidGasDisposition: IProductType;
  LiquidsShrinkageInletRate: IProductType;
  LiquidsShrinkageOutletRate: IProductType;
  AcidGasShrinkageInletRate: IProductType;
  AcidGasShrinkageOutletRate: IProductType;
  AcidGasDispositionInletRate: IProductType;
  AcidGasDispositionOutletRate: IProductType;
  FuelInletRate: IProductType;
  FuelOutletRate: IProductType;
  FlaredInletRate: IProductType;
  FlaredOutletRate: IProductType;
  VentedInletRate: IProductType;
  VentedOutletRate: IProductType;
  FFV: IProductType;
  FFVInletRate: IProductType;
  FFVOutletRate: IProductType;
  // InNetworkInlet: IProductType;
  // InNetworkOutlet: IProductType;
  GasInlet: IProductType;
  GasOutlet: IProductType;
  SalesOutlet: IProductType;
  OutOfNetworkInlet: IProductType;
}

export type PressureChartSettings = {
  product: string;
  productivityIndex: boolean;
};

export const WellProductGroups: ChartProductGroupT[] = [
  "Sales",
  "Production",
  "Injection"
];
export const MidstreamProductGroups: ChartProductGroupT[] = ["Throughput", "Shrinkage"];

export type SelectionMode = "polygon" | "none" | "rect" | "clear";
export type LegendPosition = "NE" | "NW" | "SE" | "SW";
export type LegendT = { visible: boolean; position: LegendPosition };

export interface IChartSettings {
  showP10: boolean;
  showP50: boolean;
  showP90: boolean;
  showUnitSlope: boolean;
  showHalfSlope: boolean;
  showQuarterSlope: boolean;
  stackedBarCalculation: CalculationMethod;
  pressureChartSettings: PressureChartSettings;
  selection?: SelectionMode;
  id: string;
  key?: string;
  chartType: ChartTypeLabels;
  chartTypeId: number;
  product: string;
  otherProducts?: string[];
  screenshot: boolean;
  groupByListVisible: boolean;
  colorPalette?: IColorPalette;
  useChartFocus?: boolean;
  groupBy?: GlobalGroupBy;
  showToolbar: boolean;
  showNormalize: boolean;
  enableZoom: boolean;
  forecast: boolean;
  average: boolean;
  typewells: boolean;
  averageSettings?: AverageSeriesStyle;
  p10Settings?: AverageSeriesStyle;
  p50Settings?: AverageSeriesStyle;
  p90Settings?: AverageSeriesStyle;
  unitSlopeStyle?: SlopeLineStyle;
  halfSlopeStyle?: SlopeLineStyle;
  quarterSlopeStyle?: SlopeLineStyle;
  producing: boolean;
  showOnlySelectedWells: boolean;
  normalizeBy: INormalizeBySetting;
  useNormalizeBy: boolean;
  showActiveWellOnly: true;
  legend: LegendT;
  timeStep: TimeStep;
  legendPosition: LegendPosition;
  palette: boolean;
  snapshot: boolean;
  source: EvaSource;
  fullscreen: boolean;
  sum: boolean;
  cutoff: number;
  shutInMonths: number;
  survivorBias: boolean;
  useWeightedRatioAverage: boolean;
  rangeVisible: boolean;
  xAxisParamColor: string;
  yAxisParamColor: string;
  xAxisParamVisible: boolean;
  log: {
    x: boolean;
    y: boolean;
  };
  yAxisParamVisible: boolean;
  xAxisParamListVisible: boolean;
  yAxisParamListVisible: boolean;
  busy: boolean;
  range: { x: []; y: [] };
  chartTypeParam: {
    x: {
      id: string;
      property: string;
      title: string;
      dataType: string;
      canBin: boolean;
      bin?: mBinSize;
      isForecastToggleOn?: boolean;
      forecastFolder?: string;
      forecastPropertySuffix?: string;
      product?: string;
      defaultUnit?: string;
    };
    y: {
      id: string;
      property: string;
      title: string;
      dataType: string;
      canBin: boolean;
      isForecastToggleOn?: boolean;
      forecastFolder?: string;
      forecastPropertySuffix?: string;
      product?: string;
      defaultUnit?: string;
    };
  };
  showWellCount?: boolean;
  lockUnits?: boolean;
  showLinesOfBestFit?: boolean;
  debugMode?: boolean;
  showScatter?: boolean;
  axisMinMax: IAxisMinMax;
  isDefaultLockOn?: boolean;
  showHistogram?: boolean;
  dotSize: number;
  dotOpacity: number;
  yAxisTitle?: string;
  movingAverageDays: number;
  showAnnotations: boolean;
  title?: string;
  xAxisTitle?: string;
}

export interface IChartSeriesStyle {
  color: number;
  dashed: boolean;
  hexColor: string;
  itemColors: string[] | number[];
  thickness: number;
}

export interface Annotation {
  xValue: string | number | undefined;
  comment: string;
}
export interface IChartSeries {
  annotations: Annotation[];
  isForecast: boolean;
  isSecondaryYAxis: boolean;
  label: string;
  mode: string;
  style: IChartSeriesStyle;
  type: string;
  x: number[] | string[];
  y: number[] | string[];
  xAxisIndex: number;
  yAxisIndex: number;
  dates?: string[];
  uwiIndices?: number[];
  isComparisonForecast?: boolean;
  groupTitle?: string;
  equation?: string;
  rSquared?: number;
}

export interface EChartsSeries {
  id: string;
  name: string;
  group: string;
  type: string;
  animation: boolean;
  itemStyle: {
    color: string | unknown;
    borderColor?: string;
    opacity?: number;
  };
  blendMode: string;
  large: boolean;
  largeThreshold: number;
  progressive?: number;
  line: {
    originalWidth: number;
    originalColor: string;
  };
  showSymbol: boolean;
  lineStyle: {
    color?: string;
    width?: number;
    type?: "dotted" | "dashed" | "solid";
  };
  data: (string | number | Record<string, unknown>)[];
  isForecast?: boolean;
  dimensions?: string[];
  encode?: {
    x: string;
    y: string;
  };
  xAxisIndex?: number;
  yAxisIndex?: number;
  symbolSize?: number;
  symbol?: string;
  z?: number;
  emphasis?: Record<string, unknown>;
  radius?: string;
  center?: string[];
  stack?: string;
  areaStyle?: Record<string, unknown>;
  isComparisonForecast?: boolean;
  markLine?: Record<string, unknown>;
}

export interface IChartScreenshotSettings extends IScreenshotSettings {
  /** EVA-430 box plot Data Range subtitle — default ON; read with `=== false`. */
  showDataRangeSubtitle?: Maybe<boolean>;
  axisFontSize?: Maybe<number>;
  titleFontSize?: Maybe<number>;
  axisLabelFontSize?: Maybe<number>;
  titleText?: Maybe<string>;
  showAxisTitle?: Maybe<boolean>;
  xAxisTitleText?: Maybe<string>;
  yAxisTitleText?: Maybe<string>;
  axisFontWeight?: Maybe<number>;
  showAxisLabels?: Maybe<boolean>;
  legendShowCounts?: Maybe<boolean>;
  legendOpacity?: Maybe<number>;
  legendType?: "bottom" | "inplot";
  legendPosition?: "NW" | "NE" | "SW" | "SE";
  legendShowTypeWellRescat?: Maybe<boolean>;
  fontFamily?: Maybe<string>;
  gridShowXLines?: Maybe<boolean>;
  gridShowXMinorLines?: Maybe<boolean>;
  gridShowYLines?: Maybe<boolean>;
  gridShowYMinorLines?: Maybe<boolean>;
  gridLeft?: Maybe<number>;
  gridTop?: Maybe<number>;
  gridRight?: Maybe<number>;
  gridBottom?: Maybe<number>;
  nameGapX?: Maybe<number>;
  nameGapY?: Maybe<number>;
  applyAbbreviations?: Maybe<boolean>;
  /** EVA-127 "Simplify Custom Field Names" screenshot toggle — default OFF; read with `?? false`. */
  simplifyCustomFieldNames?: Maybe<boolean>;
  xLabelRotation?: Maybe<0 | 45 | 90>;
  showProductLegend?: Maybe<boolean>;
  mergeSameUnitAxes?: Maybe<boolean>;
}

export interface ValueLabel {
  value: string;
  label: string;
}

export interface ChartResponseAxis {
  data: (string | number)[];
  defaultAxisType: string;
  interval: number;
  isSecondaryYAxis: boolean;
  labels: ValueLabel[];
  max: number;
  min: number;
  logMin?: number;
  title: string;
  yScaleToX: number;
  axisIsDate: boolean;
  /** Combined MPC x-axes only: ordered product segments for the inline legend. */
  productSegments?: AxisProductSegment[];
  /**
   * Stacked bar x-axis only: each category's Max Bin Criteria value (its count, or the
   * sum of the criteria field), keyed by the category label. Absent under Split Year.
   */
  maxBinsSortValues?: Record<string, number>;
}

/** One product on a combined MPC x-axis: its title text + the key for style lookup. */
export interface AxisProductSegment {
  label: string;
  productKey: string;
}

export interface ChartLayout {
  xAxis: ChartResponseAxis;
  yAxis: ChartResponseAxis;
  yAxis2: ChartResponseAxis[];
  xAxis2: ChartResponseAxis[];
}

export interface ChartResponseModel {
  title?: string;
  layout: ChartLayout;
  series: IChartSeries[];
  uwis?: string[];
}
